import {
	dateToQuery,
	dateToUTCString,
	localOrUtcTimestampToLocalDate,
	parseDateFromQuery,
	startOf,
	utcTimestampToLocalDate,
	utcTimestampToLocalDateString,
	utcTimestampToLocalDateTimeString,
} from "@sembark-travel/datetime-utils"
import {
	AsyncSelect,
	Badge,
	Box,
	Button,
	Col,
	Component,
	Dropdown,
	Grid,
	Heading,
	Icons,
	Inline,
	joinAttributes,
	RelativeTime,
	Select,
	Stack,
	TabContent,
	TabItem,
	Table,
	Tabs,
	TabsList,
	Text,
	Time,
	TimeDuration,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import { DatePickerField, SelectField } from "@sembark-travel/ui-form"
import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { Fragment, useCallback, useEffect } from "react"
import { Email, PhoneNumber } from "../Contacts"
import { ITrip } from "../Trips"
import {
	getEntryTypeDisplayName,
	getTypeOfVisaDisplayName,
	getTypeOfVisaOption,
	TYPE_OF_VISA_OPTIONS,
	visaProcessingTimeStr,
} from "../Visa"
import { SelectWorldCountries, TWorldCountry } from "../World"
import {
	ITripVisaBooking,
	TBookingLocation,
	TBookingState,
	TVisaBooking,
	XHR,
} from "./store"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { EditTripOwners } from "../TripOwners"
import {
	BookingInfo,
	DownloadVisaBookings,
	LatestBookingStage,
	VisaTypeDisabled,
} from "./Item"
import { isBookingPaid, REJECTED_OPTIONS } from "./utils"
import { ISupplier, SelectVisaSuppliers } from "../Supplier"
import { SelectTripDestination, TTripDestination } from "../TripDestinations"
import { generatePath } from "../router-utils"

const visaBookingStages: { value: TBookingState | "all"; label: string }[] = [
	{ value: "initialized", label: "New" },
	{ value: "document_pending", label: "Document Pending" },
	{ value: "document_received", label: "Document Received" },
	{ value: "document_verified", label: "Document Verified" },
	{ value: "appointment_scheduled", label: "Appointment Scheduled" },
	{ value: "appointment_done", label: "Appointment Done" },
	{ value: "in_progress", label: "In Progress" },
	{ value: "approved", label: "Approved" },
	{ value: "rejected", label: "Rejected" },
	{ value: "dropped", label: "Dropped" },
	{ value: "all", label: "All" },
]

type TFilters = TSearchParams & {
	report_type: "guest_wise" | "trip_wise"
	stage: TBookingState | "all"
	types_of_visas?: { name: string; value: string }[]
	countries?: TWorldCountry[]
	suppliers?: ISupplier[]
	destinations?: TTripDestination[]
	trip_starting_till?: string | Date
	date_of_appointment?: string | Date
	reason_for_rejection?: string
	appointment_location?: TBookingLocation
}

type TFiltersInQuery = TSearchParams & {
	report_type: "guest_wise" | "trip_wise"
	stage: TBookingState | "all"
	countries?: Array<string>
	suppliers?: Array<string>
	destinations?: Array<string>
	types_of_visas?: Array<string>
	trip_starting_till?: string
	date_of_appointment?: string
	reason_for_rejection?: string
	appointment_location?: string
}

type TFiltersInAPI = TSearchParams & {
	pagination: "paginate"
	stage?: TBookingState
	stages?: TBookingState[]
	countries?: Array<string>
	suppliers?: Array<string>
	destinations?: Array<string>
	types_of_visas?: Array<string>
	trip_starting_till?: string
	date_of_appointment?: string
	reason_for_rejection?: string
	appointment_location?: string
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const {
		q,
		page,
		report_type = "guest_wise",
		stage = "initialized",
		types_of_visas,
		countries,
		suppliers,
		destinations,
		trip_starting_till,
		date_of_appointment,
		reason_for_rejection,
		appointment_location,
	} = filters

	const query: TFiltersInQuery = {
		stage: stage,
		report_type: report_type,
	}

	if (countries?.length) {
		query.countries = countries.map((t) => `${t.id}_${t.name}`)
	}

	if (suppliers?.length) {
		query.suppliers = suppliers.map((t) => `${t.id}_${t.name}`)
	}

	if (destinations?.length) {
		query.destinations = destinations.map((t) => `${t.id}_${t.name}`)
	}

	if (types_of_visas) {
		query.types_of_visas = types_of_visas.map((t) => t.value)
	}

	if (trip_starting_till) {
		query.trip_starting_till = dateToQuery(trip_starting_till)
	}

	if (date_of_appointment) {
		query.date_of_appointment = dateToQuery(date_of_appointment)
	}

	if (reason_for_rejection) {
		query.reason_for_rejection = reason_for_rejection
	}

	if (appointment_location) {
		query.appointment_location = `${appointment_location.id}_${appointment_location.name}`
	}

	if (q) {
		query.q = q
	}

	if (page) {
		query.page = page
	}
	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const {
		report_type = "guest_wise",
		stage = "initialized",
		q,
		page,
		types_of_visas,
		countries,
		suppliers,
		destinations,
		trip_starting_till,
		date_of_appointment,
		reason_for_rejection,
		appointment_location,
	} = query

	const filters: TFilters = {
		stage: stage,
		report_type: report_type,
		types_of_visas:
			types_of_visas
				?.map((t) => getTypeOfVisaOption(t))
				.filter((v): v is { name: string; value: string } => v !== undefined) ??
			[],
	}
	if (countries?.length) {
		filters.countries = countries.map((d) => {
			const slices = d.split("_")
			return { id: Number.parseInt(slices[0]), name: slices.slice(1).join("_") }
		}) as unknown as Array<TWorldCountry>
	}
	if (suppliers?.length) {
		filters.suppliers = suppliers.map((d) => {
			const slices = d.split("_")
			return { id: Number.parseInt(slices[0]), name: slices.slice(1).join("_") }
		}) as unknown as Array<ISupplier>
	}
	if (destinations?.length) {
		filters.destinations = destinations.map((d) => {
			const slices = d.split("_")
			return { id: Number.parseInt(slices[0]), name: slices.slice(1).join("_") }
		}) as unknown as Array<TTripDestination>
	}
	if (trip_starting_till)
		filters.trip_starting_till = parseDateFromQuery(trip_starting_till)
	if (date_of_appointment)
		filters.date_of_appointment = parseDateFromQuery(date_of_appointment)
	if (reason_for_rejection) filters.reason_for_rejection = reason_for_rejection
	if (appointment_location) {
		const slices = appointment_location.split("_")
		filters.appointment_location = {
			id: Number.parseInt(slices[0]),
			name: slices.slice(1).join("_"),
		} as unknown as TBookingLocation
	}
	if (q) {
		filters.q = q
	}

	if (page) {
		filters.page = page
	}

	return filters
}

function filtersToAPIParams({
	stage,
	report_type,
	types_of_visas,
	countries,
	suppliers,
	destinations,
	trip_starting_till,
	date_of_appointment,
	reason_for_rejection,
	appointment_location,
	...others
}: TFilters): TFiltersInAPI {
	const params: TFiltersInAPI = {
		...others,
		pagination: "paginate",
	}

	if (stage !== "all") {
		if (report_type === "trip_wise") {
			params.stage = stage
		} else if (report_type === "guest_wise") {
			params.stages = [stage]
		}
	}

	if (report_type === "trip_wise") return params

	if (countries?.length) {
		params.countries = countries.map((c) => `${c.id}`)
	}
	if (suppliers?.length) {
		params.suppliers = suppliers.map((s) => `${s.id}`)
	}

	if (destinations?.length) {
		params.destinations = destinations.map((s) => `${s.id}`)
	}

	if (types_of_visas) {
		params.types_of_visas = types_of_visas.map((t) => t.value)
	}

	if (trip_starting_till) {
		params.trip_starting_till = dateToUTCString(
			startOf(trip_starting_till, "day")
		)
	}

	if (date_of_appointment) {
		params.date_of_appointment = dateToUTCString(
			date_of_appointment,
			"YYYY-MM-DD"
		)
	}

	if (reason_for_rejection) {
		params.reason_for_rejection = reason_for_rejection
	}
	if (appointment_location) {
		params.appointment_location = `${appointment_location.id}`
	}

	return params
}

export function Filters({ params }: { params: TFilters }) {
	return (
		<Stack gap="4">
			<SelectField
				label="Type of Visa"
				name={`types_of_visas`}
				select={Select}
				options={TYPE_OF_VISA_OPTIONS}
				multiple
			/>
			<SelectField
				label="Country"
				name="countries"
				select={SelectWorldCountries}
				fullWidth
				multiple
			/>
			<SelectField
				label="Supplier"
				name="suppliers"
				select={SelectVisaSuppliers}
				fullWidth
				multiple
			/>
			<SelectField
				label="Destinations"
				name="destinations"
				select={SelectTripDestination}
				multiple
				placeholder="Select destinations..."
			/>
			{(params.stage === "appointment_scheduled" || params.stage === "all") && (
				<>
					<SelectField
						label="Appointment Locations"
						name="appointment_location"
						select={SelectAppointmentLocations}
						placeholder="Select destinations..."
					/>
					<DatePickerField
						label="Appointment Date"
						name={`date_of_appointment`}
						dateFormat
					/>
				</>
			)}
			{(params.stage === "rejected" || params.stage === "all") && (
				<SelectField
					label="Reject Reason"
					name="reason_for_rejection"
					select={Select}
					placeholder="Select destinations..."
					options={REJECTED_OPTIONS}
				/>
			)}
			<DatePickerField
				label="Trip Starting Till"
				name={`trip_starting_till`}
				dateFormat
				timeFormat
			/>
		</Stack>
	)
}

export function VisaBookingsList() {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const getFilters = useCallback(() => <Filters params={params} />, [params])
	return (
		<Box>
			<Search
				title={
					<Box
						fontSize={{
							xs: "md",
							xl: "lg",
						}}
					>
						{params.report_type === "guest_wise" ? (
							<Inline gap="3" alignItems={"center"}>
								Guest Wise Visa Bookings
							</Inline>
						) : (
							<Inline gap="3" alignItems={"center"}>
								Trip Wise Visa Bookings
							</Inline>
						)}
					</Box>
				}
				initialParams={params}
				onSearch={setParams}
				Filters={params.report_type === "trip_wise" ? undefined : getFilters}
				resetParams={(params) => ({
					q: "",
					page: 1,
					report_type: params.report_type,
					stage: params.stage,
					country: [],
				})}
				areAdvancedFiltersApplied={(params) => {
					const { report_type, stage, ...others } = params

					return params.report_type === "guest_wise"
						? areAdvancedFiltersAppliedDefault(others)
						: false
				}}
				actions={({ setSearchParams, searchParams }) => (
					<Inline gap="2" flexWrap="wrap" justifyContent="end">
						<Button
							onClick={() => {
								setSearchParams({
									...searchParams,
									page: 1,
									report_type:
										searchParams.report_type === "guest_wise"
											? "trip_wise"
											: "guest_wise",
								})
							}}
							status="primary"
						>
							{searchParams.report_type === "guest_wise"
								? "Trip Wise"
								: "Guest Wise"}
						</Button>
						{searchParams.report_type === "guest_wise" ? (
							<Dropdown alignRight>
								<Dropdown.ToggleButton level="tertiary" size="sm">
									<Icons.DotsVertical />
								</Dropdown.ToggleButton>
								<Dropdown.Menu>
									<DownloadVisaBookings params={filtersToAPIParams(params)} />
								</Dropdown.Menu>
							</Dropdown>
						) : null}
					</Inline>
				)}
			>
				{({ searchParams, setSearchParams }) => (
					<Tabs>
						<TabsList>
							{visaBookingStages.map((o) => (
								<TabItem
									key={o.label}
									active={searchParams.stage === o.value}
									onClick={() => {
										setSearchParams({
											...searchParams,
											page: 1,
											report_type: searchParams.report_type,
											stage: o.value,
										})
									}}
								>
									{o.label}
								</TabItem>
							))}
						</TabsList>
						<TabContent>
							{searchParams.report_type === "guest_wise" ? (
								<GuestWiseBookings params={params} setParams={setParams} />
							) : (
								<TripWiseBookings params={params} setParams={setParams} />
							)}
						</TabContent>
					</Tabs>
				)}
			</Search>
		</Box>
	)
}

function VisaServiceDetailDialog({
	children,
	onSuccess,
	booking,
	trip,
}: {
	trip: ITrip
	booking: TVisaBooking
	children: (props: { open: () => void }) => React.ReactNode
	onSuccess?: () => void
}) {
	const [isOpen, open, close] = useDialog()
	const { visa_service, comments } = booking
	const {
		id,
		tourist,
		no_of_adults,
		no_of_children,
		trip_source,
		trip_source_contact,
	} = trip
	const isAgent = trip_source.is_agent
	const sourceName = isAgent
		? trip_source_contact?.name
		: tourist
			? tourist.name
			: null
	const sourcePhoneNumbers =
		isAgent && trip_source_contact
			? trip_source_contact.phone_numbers
			: tourist
				? tourist?.phone_numbers
				: null
	return (
		<>
			{children({ open })}
			<Dialog open={isOpen} onClose={close} lg title="Visa Service">
				<Dialog.Body>
					<Stack gap="6">
						<Inline bgColor="subtle" padding="4" rounded="md">
							<Grid noGutters flexWrap="wrap" gap="4">
								<Col md={4}>
									<Stack style={{ width: "47%" }}>
										<Text fontWeight="semibold" fontSize="md">
											{visa_service.name}
										</Text>
										{comments ? (
											<Inline as="span" gap="1">
												<Box>
													<Icons.Annotation color="muted" />
												</Box>
												<Text as="span" fontSize="base" color="muted">
													{comments}
												</Text>
											</Inline>
										) : null}
									</Stack>
								</Col>
								<Col>
									<Inline
										gap="6"
										justifyContent={{ xs: "start", md: "end" }}
										flexWrap="wrap"
									>
										<Stack gap="2">
											<Inline alignItems="center" minWidth="max">
												{joinAttributes(
													tourist ? (
														<Inline alignItems="center" gap="1">
															<Icons.User size="5" color="muted" />
															<Text fontWeight="semibold" fontSize="md">
																{tourist.name}
															</Text>
															<PhoneNumber
																iconOnly
																value={tourist.phone_number}
															/>
														</Inline>
													) : null,
													no_of_adults > 0 || no_of_children > 0 ? (
														<Text
															fontWeight="semibold"
															fontSize="sm"
															color="muted"
														>
															{[
																no_of_adults > 0 ? `${no_of_adults}A` : null,
																no_of_children > 0
																	? `${no_of_children}C`
																	: null,
															]
																.filter(Boolean)
																.join(", ")}
														</Text>
													) : null
												)}
											</Inline>
											<Inline alignItems="center" paddingLeft="6">
												{joinAttributes(
													<Inline alignItems="center" gap="1">
														<Text fontSize="base" color="accent">
															Trip {id}
														</Text>
													</Inline>,
													sourceName ? (
														<Text fontSize="base" color="muted">
															{sourceName}
														</Text>
													) : null,
													sourcePhoneNumbers ? (
														<PhoneNumber iconOnly value={sourcePhoneNumbers} />
													) : null
												)}
											</Inline>
										</Stack>
										<Stack gap="2">
											<Text>Payment Status</Text>
											<BookingsStatusBadge booking={booking} />
										</Stack>
										<Stack gap="2">
											<Text>Ops Team</Text>
											{trip.operations_team?.length ? (
												<Text textOverflow="truncate">
													{trip.operations_team
														.map((t, _, arr) =>
															arr.length > 1 ? t.name.slice(0, 4) : t.name
														)
														.join(", ")}
												</Text>
											) : (
												<Text>N/A</Text>
											)}
										</Stack>
									</Inline>
								</Col>
							</Grid>
						</Inline>

						<Stack gap="4">
							<Text fontSize="md" fontWeight="semibold">
								SERVICES TYPE
							</Text>
							<Stack gap="4">
								<BookingInfo booking={booking} />
							</Stack>
						</Stack>
					</Stack>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function BookingsStatusBadge({ booking }: { booking: TVisaBooking }) {
	// Returns true if ALL bookings have payment completed (are "booked")
	// Uses isBookingPaid for each booking; expects a boolean for each.
	const allPaid = isBookingPaid(booking)
	return (
		<Box>
			<Badge {...(allPaid ? { success: true } : { danger: true })}>
				{allPaid ? "Paid" : "Payment Due"}
			</Badge>
		</Box>
	)
}

function GuestWiseBookings({
	params,
	setParams,
}: {
	params: TFilters
	setParams: (params: TFilters) => void
}) {
	const { xs, sm, md } = useBreakpoints()
	return (
		<ListView<TVisaBooking & { trip: ITrip }, TFilters>
			pageKey="guest-wise-visa-bookings"
			params={params}
			onPageChange={(page) => setParams({ ...params, page })}
			fetch={(xhr, params) => {
				return XHR(xhr).getGuestWiseBookings(filtersToAPIParams(params))
			}}
		>
			{({ items, refresh }) =>
				!sm && !xs && !md ? (
					<GuestWiseBookingsDesktopView bookings={items} refresh={refresh} />
				) : (
					<GuestWiseBookingsMobileView bookings={items} refresh={refresh} />
				)
			}
		</ListView>
	)
}

function GuestWiseBookingsDesktopView({
	bookings,
	refresh,
}: {
	bookings: (TVisaBooking & { trip: ITrip })[]
	refresh: () => void
}) {
	return (
		<Table
			hover
			responsive
			headers={[
				"Guest Details",
				"Country",
				"Visa type",
				"Visa Details",
				"Status & Updated By",
				"Comments",
			]}
			bordered
			alignCols={{
				6: "right",
			}}
			rows={bookings.map((booking) => {
				const {
					tourists,
					visa_type,
					latest_booking_stage,
					comments,
					trip_id,
					country,
					can_modify,
					trip,
				} = booking
				const tourist = tourists[0] ?? {}
				const canEdit = Boolean(can_modify && !trip.is_locked)
				return [
					<Stack>
						<Link
							to={`/trips/${trip_id}/services-bookings/visas#visa_bookings`}
							anchored
							color="accent"
						>
							<Text color="accent" fontWeight="semibold">
								{tourist?.name ?? "N/A"}
							</Text>
						</Link>
						<Inline color="muted">
							{joinAttributes(
								`#${trip_id}`,
								tourist.phone_numbers?.length ? (
									<Stack gap="1">
										{tourist.phone_numbers
											?.filter((pn) => Boolean(pn.number))
											?.map((contact) => (
												<PhoneNumber value={contact.phone_number} iconOnly />
											))}
									</Stack>
								) : null,
								tourist.email ? <Email value={tourist.email} iconOnly /> : null
							)}
						</Inline>
					</Stack>,
					<Text>{country?.name || "N/A"}</Text>,
					<Stack gap="1">
						<Text>{visa_type?.name || "N/A"}</Text>
						<VisaTypeDisabled visaType={visa_type} />
					</Stack>,
					<Inline flexWrap="wrap">
						{joinAttributes(
							visa_type?.duration_formatted ? (
								<Text>Duration {visa_type.duration_formatted}</Text>
							) : null,
							visa_type?.processing_time_in_hours ? (
								<Text>
									Processed in{" "}
									{visaProcessingTimeStr(visa_type.processing_time_in_hours)}
								</Text>
							) : null,
							visa_type?.apply_before_days ? (
								<Text>
									Apply Minimum {visa_type?.apply_before_days} Days Prior
								</Text>
							) : null
						)}
					</Inline>,
					<Stack gap="1">
						<Inline gap="1" alignItems="center">
							<LatestBookingStage
								booking={booking}
								minimal
								onSuccess={refresh}
								canEdit={canEdit}
							/>
						</Inline>
						{latest_booking_stage.comments ? (
							<Inline fontWeight="normal" fontSize="sm" gap="1" maxWidth="xs">
								<Text>
									<Icons.Annotation color="muted" />{" "}
									{latest_booking_stage.comments}
								</Text>
							</Inline>
						) : null}
						{latest_booking_stage.created_at ||
						latest_booking_stage.created_by ? (
							<Text fontSize="sm" color="muted">
								{latest_booking_stage.created_by
									? ` by ${latest_booking_stage.created_by.name}`
									: null}{" "}
								{latest_booking_stage.created_at
									? utcTimestampToLocalDateString(
											latest_booking_stage.created_at
										)
									: null}
							</Text>
						) : null}
					</Stack>,
					comments ?? "-",
				]
			})}
		/>
	)
}

function GuestWiseBookingsMobileView({
	bookings,
	refresh,
}: {
	bookings: (TVisaBooking & { trip: ITrip })[]
	refresh: () => void
}) {
	return (
		<Box as="ol">
			{bookings.map((booking) => {
				const {
					id,
					tourists,
					visa_type,
					latest_booking_stage,
					comments,
					trip_id,
					country,
					can_modify,
					trip,
				} = booking
				const tourist = tourists[0] ?? {}
				const canEdit = Boolean(can_modify && !trip.is_locked)

				return (
					<Box
						as="li"
						key={id}
						marginBottom="4"
						rounded="md"
						borderWidth="1"
						bgColor="default"
						boxShadow="base"
					>
						<Box padding="4" borderBottomWidth="1">
							<Inline gap="2" alignItems="center" flexWrap="wrap">
								<Link
									to={`/trips/${trip_id}/services-bookings/visas#visa_bookings`}
									anchored
									color="accent"
								>
									<Text color="accent" fontWeight="semibold" fontSize="base">
										{tourist?.name ?? "N/A"}
									</Text>
								</Link>
								<Inline color="muted" fontSize="sm">
									{joinAttributes(
										`#${trip_id}`,
										tourist.phone_numbers?.length ? (
											<Stack gap="1">
												{tourist.phone_numbers
													?.filter((pn) => Boolean(pn.number))
													?.map((contact) => (
														<PhoneNumber
															value={contact.phone_number}
															iconOnly
														/>
													))}
											</Stack>
										) : null,
										tourist.email ? (
											<Email value={tourist.email} iconOnly />
										) : null
									)}
								</Inline>
							</Inline>
						</Box>

						<Stack padding="4" gap="3">
							<Inline
								justifyContent="between"
								alignItems="center"
								flexWrap="wrap"
							>
								<Text fontWeight="semibold" fontSize="base">
									{country?.name || "N/A"}
								</Text>
							</Inline>

							<Stack gap="1">
								<Text fontSize="base">{visa_type?.name || "N/A"}</Text>
								<VisaTypeDisabled visaType={visa_type} />
							</Stack>

							{(visa_type?.duration_formatted ||
								visa_type?.processing_time_in_hours ||
								visa_type?.apply_before_days) && (
								<Inline flexWrap="wrap" fontSize="sm" color="muted">
									{joinAttributes(
										visa_type?.duration_formatted ? (
											<Text fontSize="sm">
												Duration {visa_type.duration_formatted}
											</Text>
										) : null,
										visa_type?.processing_time_in_hours ? (
											<Text fontSize="sm">
												Processed in{" "}
												{visaProcessingTimeStr(
													visa_type.processing_time_in_hours
												)}
											</Text>
										) : null,
										visa_type?.apply_before_days ? (
											<Text fontSize="sm">
												Apply Minimum {visa_type?.apply_before_days} Days Prior
											</Text>
										) : null
									)}
								</Inline>
							)}

							<Stack gap="1" paddingTop="2" borderTopWidth="1">
								<Inline gap="1" alignItems="center">
									<LatestBookingStage
										booking={booking}
										minimal
										onSuccess={refresh}
										canEdit={canEdit}
									/>
								</Inline>
								{latest_booking_stage.comments ? (
									<Inline
										fontWeight="normal"
										fontSize="sm"
										gap="1"
										maxWidth="full"
									>
										<Text>
											<Icons.Annotation color="muted" />{" "}
											{latest_booking_stage.comments}
										</Text>
									</Inline>
								) : null}
								{latest_booking_stage.created_at ||
								latest_booking_stage.created_by ? (
									<Text fontSize="sm" color="muted">
										{latest_booking_stage.created_by
											? ` by ${latest_booking_stage.created_by.name}`
											: null}{" "}
										{latest_booking_stage.created_at
											? utcTimestampToLocalDateString(
													latest_booking_stage.created_at
												)
											: null}
									</Text>
								) : null}
							</Stack>

							{comments ? (
								<Box
									fontSize="sm"
									color="muted"
									paddingTop="2"
									borderTopWidth="1"
								>
									<Text fontSize="sm">
										<Icons.Annotation color="muted" /> {comments}
									</Text>
								</Box>
							) : null}
						</Stack>
					</Box>
				)
			})}
		</Box>
	)
}

function TripWiseBookings({
	params,
	setParams,
}: {
	params: TFilters
	setParams: (params: TFilters) => void
}) {
	const { xs, sm, md } = useBreakpoints()
	return (
		<ListView<ITripVisaBooking, TFilters>
			pageKey="trip-wise-visa-bookings"
			params={params}
			onPageChange={(page) => setParams({ ...params, page })}
			fetch={(xhr, params) => {
				return XHR(xhr).getTripWiseBookings(filtersToAPIParams(params))
			}}
		>
			{({ items: trips, refresh }) =>
				!sm && !xs && !md ? (
					<TripWiseBookingsDesktopView trips={trips} onSuccess={refresh} />
				) : (
					<TripWiseBookingsMobileView trips={trips} onSuccess={refresh} />
				)
			}
		</ListView>
	)
}

function TripWiseBookingsDesktopView({
	trips,
	onSuccess,
}: {
	trips: ITripVisaBooking[]
	onSuccess: () => void
}) {
	const { hasPermission } = useCheckPermissions()
	return (
		<Table className="excel-style-table" responsive>
			<thead>
				<tr>
					<th>Basic Details</th>
					<th>Visa</th>
					<th>Visa Details</th>
					<th>Status</th>
					<th>Comments</th>
				</tr>
			</thead>
			<tbody>
				{trips.map((trip) => {
					const {
						id: tripId,
						trip_source,
						reference_id,
						destinations,
						no_of_adults,
						children,
						contact,
						days,
						converted_at,
						converted_by,
						on_hold_at,
						on_hold_by,
						visa_bookings,
						is_locked,
					} = trip
					const start_date = localOrUtcTimestampToLocalDate(
						trip.start_date_local,
						trip.start_date
					)
					const bookingRowsCount = visa_bookings.length + 1
					return (
						<Fragment key={`trip_${tripId}`}>
							<Box as="tr">
								<Box as="td" rowSpan={bookingRowsCount}>
									<Stack maxWidth="xs" gap="1" style={{ minWidth: "200px" }}>
										<Heading fontSize="md" as="h3">
											<Link
												to={generatePath(
													"/trips/:tripId/services-bookings/:serviceType",
													{
														tripId: String(tripId),
														serviceType: "visas#visa_bookings",
													}
												)}
												anchored
												color="accent"
											>
												{contact.name} •{" "}
												<Box display="inline" as="span">
													{destinations.map((l) => l.short_name).join(", ")}
												</Box>
											</Link>
										</Heading>
										<Box fontSize="sm">
											{joinAttributes(
												<Box display="inline">
													<Box display="inline" marginRight="1" color="muted">
														#
													</Box>
													{tripId}
												</Box>,
												<Box display="inline" title={trip_source.name}>
													{trip_source.short_name}
													{reference_id ? `-${reference_id}` : ``}
												</Box>
											)}
										</Box>
										<Box>
											{joinAttributes(
												<Time value={start_date} />,
												<TimeDuration days={days}>{days}D</TimeDuration>,
												`${no_of_adults}A${
													children.length ? `, ${children.length}C` : ``
												}`
											)}
										</Box>
										{converted_at && converted_by ? (
											<Box fontSize="sm" color="muted">
												<Icons.ChevronDown rotate="270" title="Conversion" />{" "}
												<RelativeTime
													value={utcTimestampToLocalDate(converted_at)}
												/>{" "}
												by {converted_by.name}
											</Box>
										) : on_hold_at && on_hold_by ? (
											<Box fontSize="sm" color="muted">
												<Badge>On-Hold Trip</Badge>{" "}
												<RelativeTime
													value={utcTimestampToLocalDate(on_hold_at)}
												/>{" "}
												by {on_hold_by.name}
											</Box>
										) : (
											<Box>
												<Badge>On-Hold Trip</Badge>
											</Box>
										)}
										<Box marginTop="4">
											<Box display="inlineBlock" marginRight="1">
												<Icons.Users />
											</Box>
											{trip.reservations_team?.length ? (
												trip.reservations_team
													.map((user) => user.name)
													.join(", ")
											) : (
												<Box color="muted" fontSize="sm" display="inline">
													Not Set
												</Box>
											)}
											{hasPermission(PERMISSIONS.MANAGE_TRIP_OWNERS) &&
											!is_locked ? (
												<Component initialState={false}>
													{({ state: isEditing, setState: setIsEditing }) => (
														<Box marginLeft="2" display="inlineBlock">
															{!isEditing ? (
																<Button
																	onClick={() => {
																		setIsEditing(true)
																	}}
																	inline
																	title="Edit operation teams"
																>
																	<Icons.Pencil />
																</Button>
															) : null}
															<Dialog
																title="Edit Reservations Team"
																open={isEditing}
																onClose={() => setIsEditing(false)}
																sm
															>
																<Dialog.Body>
																	<EditTripOwners
																		type="reservations_team"
																		users={trip.reservations_team}
																		itemId={trip.id}
																		onSuccess={() => {
																			setIsEditing(false)
																			onSuccess()
																		}}
																		onCancel={() => {
																			setIsEditing(false)
																		}}
																	/>
																</Dialog.Body>
															</Dialog>
														</Box>
													)}
												</Component>
											) : null}
										</Box>
									</Stack>
								</Box>
								<Box as="td" colSpan={5} bgColor="subtle">
									<Box display="flex" justifyContent="between">
										<Box paddingY="1" fontWeight="semibold">
											{joinAttributes(
												<Box as="span">
													<Box as="span" fontWeight="semibold" fontSize="lg">
														{
															visa_bookings.filter(
																(b) =>
																	b.latest_booking_stage.state === "approved"
															).length
														}
													</Box>
													<Box as="span" marginLeft="1">
														/ {visa_bookings.length} Approved
													</Box>
												</Box>
											)}
										</Box>
									</Box>
								</Box>
							</Box>
							{visa_bookings.map((booking) => {
								const {
									id,
									visa_service,
									visa_type,
									latest_booking_stage,
									tourists,
									can_modify,
								} = booking
								const tourist = tourists[0] || null
								const canEdit = Boolean(can_modify && !trip.is_locked)
								return (
									<Box as="tr" key={id}>
										{/* Columns for bookings -- Start --*/}
										{/* Note: Adding a column should update the header rowSpan */}
										<Box as="td" style={{ maxWidth: "200px" }}>
											<Stack gap="1">
												<Inline
													title={visa_service?.name}
													alignItems="center"
													flexWrap="wrap"
												>
													{joinAttributes(
														<Inline gap="1" alignItems="center" flexWrap="wrap">
															<VisaServiceDetailDialog
																booking={booking}
																trip={trip as ITrip}
															>
																{({ open }) => (
																	<Text
																		color="primary"
																		fontWeight="semibold"
																		fontSize="base"
																		cursor="pointer"
																		onClick={open}
																	>
																		{visa_service?.name}
																	</Text>
																)}
															</VisaServiceDetailDialog>
															<Text fontSize="xs">{visa_type.name}</Text>
														</Inline>,
														tourist ? (
															<Text key={tourist.id} fontSize="xs">
																{tourist.name}
															</Text>
														) : null
													)}
												</Inline>
												<VisaTypeDisabled visaType={visa_type} />
											</Stack>
										</Box>
										<Box as="td">
											<Inline>
												{joinAttributes(
													visa_type.duration_formatted ? (
														<Text>{visa_type.duration_formatted}</Text>
													) : null,
													<Text>
														{getTypeOfVisaDisplayName(visa_type.type_of_visa)}
													</Text>,
													<Text>
														{getEntryTypeDisplayName(visa_type.entry_type)}
													</Text>
												)}
											</Inline>
										</Box>
										<Box
											as="td"
											title={
												latest_booking_stage?.created_at
													? utcTimestampToLocalDateTimeString(
															latest_booking_stage?.created_at
														)
													: ""
											}
											verticalAlign="middle"
										>
											<Box display="flex">
												<LatestBookingStage
													booking={booking}
													minimal
													onSuccess={onSuccess}
													canEdit={canEdit}
												/>
											</Box>
										</Box>
										<Box as="td" fontSize="sm" style={{ maxWidth: "200px" }}>
											{latest_booking_stage.comments ? (
												<Box title={latest_booking_stage.comments}>
													{latest_booking_stage.comments}
												</Box>
											) : null}
										</Box>
										{/* Columns for bookings -- End --*/}
									</Box>
								)
							})}
						</Fragment>
					)
				})}
			</tbody>
		</Table>
	)
}

function TripWiseBookingsMobileView({
	trips,
	onSuccess,
}: {
	trips: ITripVisaBooking[]
	onSuccess: () => void
}) {
	const { hasPermission } = useCheckPermissions()
	return (
		<Box as="ol">
			{trips.map((trip) => {
				const {
					id: tripId,
					trip_source,
					destinations,
					no_of_adults,
					children,
					contact,
					days,
					visa_bookings,
					is_locked,
				} = trip
				const start_date = localOrUtcTimestampToLocalDate(
					trip.start_date_local,
					trip.start_date
				)
				return (
					<Box
						as="li"
						key={trip.id}
						marginBottom="8"
						rounded="md"
						borderWidth="1"
						bgColor="default"
						boxShadow="base"
					>
						<Box padding="4" borderBottomWidth="1">
							<Box display="flex" flexWrap="wrap" alignItems="baseline">
								<Box
									fontSize="lg"
									as="h3"
									whiteSpace="preserve"
									marginBottom="0"
									fontWeight="semibold"
									paddingRight="2"
								>
									<Link
										to={generatePath(
											"/trips/:tripId/services-bookings/:serviceType",
											{
												tripId: String(tripId),
												serviceType: "visas#visa_bookings",
											}
										)}
										anchored
									>
										{contact.name} •{" "}
										<Box display="inline" as="span">
											{destinations.map((l) => l.short_name).join(", ")}
										</Box>
									</Link>
								</Box>
								<Box fontSize="sm">
									{joinAttributes(
										<Box display="inline">
											<Box display="inline" marginRight="1" color="muted">
												#
											</Box>
											{tripId}
										</Box>,
										<Box display="inline" title={trip_source.name}>
											{trip_source.short_name}
											{tripId ? `-${tripId}` : ``}
										</Box>
									)}
								</Box>
							</Box>
							<Box
								display="flex"
								flexWrap="wrap"
								justifyContent="between"
								alignItems="center"
								fontSize="sm"
							>
								<Box marginY="1" whiteSpace="preserve" paddingRight="2">
									{joinAttributes(
										<Time value={start_date} />,
										<TimeDuration days={days}>{days}D</TimeDuration>,
										`${no_of_adults}A${
											children.length ? `, ${children.length}C` : ``
										}`
									)}
								</Box>
							</Box>
						</Box>
						<Box as="ul">
							<Box
								as="li"
								paddingX="4"
								paddingY="2"
								bgColor="subtle"
								display="flex"
								alignItems="center"
							>
								{joinAttributes(
									<Box as="span">
										<Box as="span" fontWeight="semibold" fontSize="lg">
											{
												visa_bookings.filter(
													(b) => b.latest_booking_stage.state === "approved"
												).length
											}
										</Box>
										<Box as="span" marginLeft="1">
											/ {visa_bookings.length} Approved
										</Box>
									</Box>,
									<Box display="inlineFlex" alignItems="center">
										<Box marginRight="1">
											<Icons.Users title="Reservations Team" />
										</Box>
										{trip.reservations_team?.length ? (
											trip.reservations_team.map((user) => user.name).join(", ")
										) : (
											<Box color="muted" fontSize="sm" display="inline">
												Not Set
											</Box>
										)}
										{hasPermission(PERMISSIONS.MANAGE_TRIP_OWNERS) &&
										!is_locked ? (
											<Component initialState={false}>
												{({ state: isEditing, setState: setIsEditing }) => (
													<>
														{!isEditing ? (
															<Box
																as="button"
																fontSize="sm"
																color="muted"
																display="inline"
																onClick={() => {
																	setIsEditing(true)
																}}
																marginLeft="2"
																title="Edit operation teams"
															>
																<Icons.Pencil />
															</Box>
														) : null}
														<Dialog
															title="Edit Operations Team"
															open={isEditing}
															onClose={() => setIsEditing(false)}
															sm
														>
															<Dialog.Body>
																<EditTripOwners
																	type="reservations_team"
																	users={trip.reservations_team}
																	itemId={trip.id}
																	onSuccess={() => {
																		setIsEditing(false)
																		onSuccess()
																	}}
																	onCancel={() => {
																		setIsEditing(false)
																	}}
																/>
															</Dialog.Body>
														</Dialog>
													</>
												)}
											</Component>
										) : null}
									</Box>
								)}
							</Box>
							{visa_bookings.map((booking) => {
								const {
									id,
									visa_service,
									visa_type,
									latest_booking_stage,
									tourists,
									can_modify,
								} = booking
								const canEdit = Boolean(can_modify && !trip.is_locked)
								const tourist = tourists[0] || null
								return (
									<Box
										key={id}
										as="li"
										borderTopWidth="1"
										paddingX="4"
										paddingY="2"
									>
										<Inline gap="1">
											<Inline
												title={visa_service?.name}
												alignItems="center"
												flexWrap="wrap"
											>
												{joinAttributes(
													<Inline gap="1" alignItems="center" flexWrap="wrap">
														<VisaServiceDetailDialog
															booking={booking}
															trip={trip as ITrip}
														>
															{({ open }) => (
																<Text
																	color="primary"
																	fontWeight="semibold"
																	fontSize="base"
																	cursor="pointer"
																	onClick={open}
																>
																	{visa_service?.name}
																</Text>
															)}
														</VisaServiceDetailDialog>
														<Text fontSize="xs">{visa_type.name}</Text>
													</Inline>,
													tourist ? (
														<Text key={tourist.id} fontSize="xs">
															{tourist.name}
														</Text>
													) : null
												)}
											</Inline>
											<VisaTypeDisabled visaType={visa_type} />
										</Inline>
										<Box
											title={
												latest_booking_stage.created_at
													? utcTimestampToLocalDateTimeString(
															latest_booking_stage.created_at
														)
													: ""
											}
											verticalAlign="middle"
										>
											<Box display="flex" fontSize="sm">
												{joinAttributes(
													<Box fontWeight="semibold">
														<LatestBookingStage
															booking={booking}
															minimal
															onSuccess={onSuccess}
															canEdit={canEdit}
														/>
													</Box>
												)}
											</Box>
										</Box>
										{latest_booking_stage.comments ? (
											<Box
												fontSize="sm"
												style={{ maxWidth: "100%" }}
												color="muted"
											>
												<Box title={latest_booking_stage.comments}>
													{latest_booking_stage.comments}
												</Box>
											</Box>
										) : null}
									</Box>
								)
							})}
						</Box>
					</Box>
				)
			})}
		</Box>
	)
}

export function SelectAppointmentLocations({
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			{...props}
			optionRenderer={({ option }) => {
				return <Box>{option.name}</Box>
			}}
			fetch={async (q: string) => {
				const response = await XHR(xhr).getBookingLocations({
					q: q,
				})
				return response.data
			}}
		/>
	)
}
